/**
 * Invoice Types and DTOs
 * Matches backend InvoiceDetailsDTO structure from commerce-service
 */

// Enums
export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  ISSUED = 'ISSUED',
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED'
}

export enum PaymentMethod {
  CASH = 'CASH',
  BANK_TRANSFER = 'BANK_TRANSFER',
  UPI = 'UPI',
  CHEQUE = 'CHEQUE',
  CARD = 'CARD',
  WALLET = 'WALLET',
  OTHER = 'OTHER'
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED'
}

export enum TaxType {
  CGST = 'CGST',
  SGST = 'SGST',
  IGST = 'IGST',
  CESS = 'CESS'
}

// Base interfaces
export interface CustomerInfo {
  customerId: number;
  customerName: string;
  email?: string;
  phone?: string;
  billingAddress?: string;
  gstin?: string;
  pan?: string;
}

export interface OrderReference {
  orderId: number;
  orderNumber: string;
  orderDate: string;
}

export interface TaxDetails {
  taxType: TaxType;
  taxRate: number;
  taxAmount: number;
}

export interface LineItemDetails {
  lineItemId: number;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discount: number;
  discountPercentage: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  taxes: TaxDetails[];
}

export interface FinancialSummary {
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  cessAmount: number;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
}

export interface PaymentRecord {
  paymentId: number;
  paymentDate: string;
  amount: number;
  paymentMethod: PaymentMethod;
  transactionReference?: string;
  status: PaymentStatus;
  notes?: string;
  recordedBy?: string;
  recordedAt: string;
  runningBalance: number;
}

// Main Invoice Details DTO
export interface InvoiceDetails {
  // Invoice Header
  invoiceId: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  status: InvoiceStatus;

  // Customer Information
  customer: CustomerInfo;

  // Order Reference (optional)
  order?: OrderReference;

  // Line Items
  lineItems: LineItemDetails[];

  // Financial Summary
  financialSummary: FinancialSummary;

  // Payment History
  payments: PaymentRecord[];

  // Additional Information
  notes?: string;
  termsAndConditions?: string;

  // Metadata
  createdBy?: string;
  createdAt: string;
  lastModifiedBy?: string;
  lastModifiedAt?: string;
}

// Request DTOs for mutations
export interface RecordPaymentRequest {
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  transactionReference?: string;
  notes?: string;
}

export interface SendInvoiceEmailRequest {
  recipientEmail?: string; // If not provided, uses customer email
  ccEmails?: string[];
  subject?: string;
  message?: string;
  includeAttachment: boolean;
}

// Response types
export interface RecordPaymentResponse {
  success: boolean;
  paymentId: number;
  message: string;
  updatedInvoice: InvoiceDetails;
}

export interface SendEmailResponse {
  success: boolean;
  message: string;
  sentAt: string;
}

// API Error response
export interface ApiErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
}

// ============================================
// OVERDUE INVOICE TYPES (US-INV-004)
// ============================================

/**
 * Summary information for overdue invoice displayed in list
 */
export interface OverdueInvoiceSummary {
  invoiceId: number;
  invoiceNumber: string;
  customerId: number;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  dueDate: string;
  daysOverdue: number;
  status: InvoiceStatus;
  location?: string;
}

/**
 * Filters for overdue invoices query
 */
export interface OverdueInvoiceFilters {
  customerId?: number;
  minAmount?: number;
  maxAmount?: number;
  minDaysOverdue?: number;
  maxDaysOverdue?: number;
  location?: string;
  page?: number;
  size?: number;
}

/**
 * Overdue summary statistics for dashboard widget
 */
export interface OverdueSummary {
  totalOverdueInvoices: number;
  totalOverdueAmount: number;
  overdueByRange: {
    lessThan7Days: { count: number; amount: number };
    between7And14Days: { count: number; amount: number };
    moreThan14Days: { count: number; amount: number };
  };
}

/**
 * Customer-wise overdue collection report
 */
export interface CustomerOverdueReport {
  customerId: number;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  totalInvoices: number;
  totalOverdueAmount: number;
  oldestDueDate: string;
  oldestInvoiceNumber: string;
}

/**
 * Aging analysis bucket
 */
export interface AgingAnalysisBucket {
  range: string; // "0-30", "31-60", "61-90", "90+"
  invoiceCount: number;
  totalAmount: number;
  percentage: number;
}

/**
 * Collection report data
 */
export interface CollectionReport {
  generatedAt: string;
  totalOverdueAmount: number;
  totalOverdueInvoices: number;
  customerWiseReport: CustomerOverdueReport[];
  agingAnalysis: AgingAnalysisBucket[];
}

/**
 * Paginated response for overdue invoices
 */
export interface PagedOverdueResponse {
  content: OverdueInvoiceSummary[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

/**
 * Send reminder email response
 */
export interface SendReminderResponse {
  success: boolean;
  message: string;
  sentTo: string;
  sentAt: string;
}

/**
 * Export report format
 */
export type ExportFormat = 'pdf' | 'excel';
